import { EUROPE_REGIONS } from 'constants/config/region'
import type { CountryDestination } from 'home/components/OfferCarousels/localDestinationOfferCarouselUtils'
import { LOCAL_DESTINATION_OFFER_MINIMUM_FLASH_OFFER_COUNT } from './localDestinationOfferCarouselConstants'

export function isLocalDestinationOfferListEligibilityResolved(
  list: App.OfferList | undefined,
): list is App.OfferList {
  return !!list && !list.fetching && !list.error
}

export function isLocalDestinationOfferListEligibilityComplete(
  list: App.OfferList | undefined,
  retriesExhausted = false,
): boolean {
  if (!list || list.fetching) {
    return false
  }

  if (!list.error) {
    return true
  }

  return retriesExhausted
}

export type LocalDestinationOfferCarouselEligibilityOptions = {
  exhaustedListKeys?: ReadonlySet<string>
  listKeys?: Array<string>
}

export function selectLocalDestinationOfferCarouselState(
  destinationsWithIds: Array<CountryDestination>,
  offerListStates: Array<App.OfferList | undefined>,
  options: LocalDestinationOfferCarouselEligibilityOptions = {},
) {
  const { exhaustedListKeys, listKeys } = options

  const isEligibilityComplete = (index: number) => {
    const list = offerListStates[index]
    const listKey = listKeys?.[index]
    const retriesExhausted = !!listKey && !!exhaustedListKeys?.has(listKey)

    return isLocalDestinationOfferListEligibilityComplete(list, retriesExhausted)
  }

  const eligibleDestinations = destinationsWithIds.filter((destination, index) => {
    const list = offerListStates[index]
    return isLocalDestinationOfferListEligibilityResolved(list)
      && list.offerIds.length >= LOCAL_DESTINATION_OFFER_MINIMUM_FLASH_OFFER_COUNT
  })

  const allChecksComplete = destinationsWithIds.length === 0 || destinationsWithIds.every((_, index) => {
    return isEligibilityComplete(index)
  })

  // Keep the skeleton until every destination is resolved (or the caller applies the timeout).
  // Freezing chips from the settled eligible set keeps pill visibility stable.
  const checkingEligibility = !allChecksComplete

  return {
    eligibleDestinations,
    checkingEligibility,
    allChecksComplete,
  }
}

export type LocalDestinationOfferCarouselEligibilityState = ReturnType<typeof selectLocalDestinationOfferCarouselState>

/**
 * End the eligibility wait once the eligibility timeout elapses. Does not force
 * `allChecksComplete` or discard already-resolved destinations; freeze empty on timeout
 * so late results cannot insert the module after the user has scrolled past.
 */
export function applyLocalDestinationOfferCarouselEligibilityTimeout(
  state: LocalDestinationOfferCarouselEligibilityState,
  eligibilityTimedOut: boolean,
): LocalDestinationOfferCarouselEligibilityState {
  if (!eligibilityTimedOut) {
    return state
  }

  return {
    ...state,
    checkingEligibility: false,
  }
}

export function shouldShowLocalDestinationOfferCarousel(options: {
  isHotelFilter: boolean
  isEnabled: boolean
}): boolean {
  const { isHotelFilter, isEnabled } = options

  return isHotelFilter && isEnabled
}

export function isLocalDestinationOfferCarouselEnabled(regionCode: string): boolean {
  return EUROPE_REGIONS.has(regionCode)
}

/** Hide only after every destination resolved with zero eligible offers — not while still checking */
export function shouldRenderLocalDestinationOfferCarouselBody(options: {
  defaultDestinationId: string | undefined
  allChecksComplete: boolean
  eligibleDestinationCount: number
}): boolean {
  const { defaultDestinationId, allChecksComplete, eligibleDestinationCount } = options

  return !!defaultDestinationId && !(allChecksComplete && eligibleDestinationCount === 0)
}
